/**
 * When to offer One Tap (ROADMAP 1.37 item 3, placement decided 2026-09-05):
 * on a page the reader is actually reading — after 15 s, or once they have
 * scrolled half of it — never on the homepage's first paint, because one
 * dismissal there suppresses the prompt site-wide for hours. Once per visit,
 * signed-out readers only, and withdrawn the moment someone signs in.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import analyticsService from '../services/analytics';
import { cancelOneTap, isReturningGoogleUser, promptOneTap } from './googleOneTap';

export const DWELL_MS = 15000;
export const SCROLL_FRACTION = 0.5;
export const PROMPTED_THIS_VISIT_KEY = 'ia:oneTapPromptedThisVisit';

function promptedThisVisit(): boolean {
  try {
    return sessionStorage.getItem(PROMPTED_THIS_VISIT_KEY) === '1';
  } catch {
    return true; // storage blocked: do not risk asking on every page
  }
}

function markPrompted(): void {
  try {
    sessionStorage.setItem(PROMPTED_THIS_VISIT_KEY, '1');
  } catch {
    // ignored
  }
}

export function oneTapAllowedOn(pathname: string): boolean {
  if (pathname === '/' || pathname === '') return false;
  return !/^\/(index_admin|oauth|auth|login|signup|plans|billing|checkout)/.test(pathname);
}

export function useGoogleOneTap(): void {
  const { user, loading, signInWithGoogleCredential } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    if (user) {
      cancelOneTap();
      return undefined;
    }
    if (!clientId || loading || !oneTapAllowedOn(pathname) || promptedThisVisit()) return undefined;

    let fired = false;
    const offer = () => {
      if (fired || promptedThisVisit()) return;
      fired = true;
      markPrompted();
      analyticsService.logEvent('one_tap_prompted', { returning: isReturningGoogleUser(), path: pathname });
      void promptOneTap(clientId, (idToken) => {
        void signInWithGoogleCredential(idToken).then((outcome) => {
          analyticsService.logEvent('one_tap_completed', { outcome });
        });
      });
    };
    const timer = window.setTimeout(offer, DWELL_MS);
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= SCROLL_FRACTION) offer();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [user, loading, pathname, signInWithGoogleCredential]);
}
